import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay/hooks';
import { CustomErrorBoundary } from 'src/components/widgets/customErrorBoundary';
import { NotificationBanner } from 'src/components/widgets/notificationBanner';
import Stack from 'src/components/widgets/stack/stack';
import { stringFormat, T, toLocalID } from 'src/shared/helpers';
import { useProductConfigurationState } from '../../state';
import type { variantsQuery$data } from './__generated__/variantsQuery.graphql';
import type { useVariantStoreOptionsQuery, useVariantStoreOptionsQuery$data } from './__generated__/useVariantStoreOptionsQuery.graphql';

export type VariantStoreOptions = useVariantStoreOptionsQuery$data['merchantProductOptions'];
export type ConnectedVariantStoreOption = VariantStoreOptions[number] & { salesChannelID: number; sku: string };

type Source = { key: string; salesChannelID: string; localSalesChannelID: number; sku: string; label: string; isMain: boolean };
type OnLoad = (key: string, options: VariantStoreOptions | undefined) => void;
const emptyOptions: VariantStoreOptions = [];

function ChannelOptionsQuery({ source, attempt, onLoad }: { source: Source; attempt: number; onLoad: OnLoad }) {
    const { merchantProductOptions } = useLazyLoadQuery<useVariantStoreOptionsQuery>(
        graphql`
            query useVariantStoreOptionsQuery($sku: String!, $salesChannelID: ID!) {
                merchantProductOptions(sku: $sku, salesChannelID: $salesChannelID) {
                    optionID
                    label
                    isGlobal
                    values {
                        valueID
                        label
                    }
                }
            }
        `,
        { sku: source.sku, salesChannelID: source.salesChannelID },
        { fetchPolicy: 'network-only', fetchKey: attempt }
    );
    useEffect(() => onLoad(source.key, merchantProductOptions), [source.key, merchantProductOptions, onLoad]);
    return null;
}

function ChannelOptions({ source, onLoad }: { source: Source; onLoad: OnLoad }) {
    const [attempt, setAttempt] = useState(0);
    return (
        <CustomErrorBoundary
            key={attempt}
            render={() => (
                <NotificationBanner
                    type='WARNING'
                    title={stringFormat(T._('Could not check variant mappings for {0}', 'Admin'), source.label)}
                    content={T._(
                        'Options could not be loaded for this sales channel. Its mappings have not been checked. Other sales channels can still be repaired.',
                        'Admin'
                    )}
                    buttons={[
                        {
                            label: T._('Retry', 'Admin'),
                            onClick: () => {
                                onLoad(source.key, undefined);
                                setAttempt((value) => value + 1);
                            }
                        }
                    ]}
                />
            )}
        >
            <Suspense fallback={<NotificationBanner type='INFO' content={stringFormat(T._('Checking variant mappings for {0}…', 'Admin'), source.label)} />}>
                <ChannelOptionsQuery source={source} attempt={attempt} onLoad={onLoad} />
            </Suspense>
        </CustomErrorBoundary>
    );
}

/** Each store request suspends and fails independently; only successful catalogs reach the repair planner. */
export function useVariantStoreOptions(salesChannels: variantsQuery$data['salesChannels']) {
    const { state } = useProductConfigurationState();
    const sources = useMemo(() => {
        const candidates = [
            { salesChannelId: state.salesChannelID, code: state.product.code || '', isMain: true },
            ...state.product.additionalCodes.map((code) => ({ ...code, isMain: false }))
        ];
        return candidates.flatMap<Source>((candidate, index) => {
            const channel = salesChannels.find((item) => toLocalID(item.id) === toLocalID(candidate.salesChannelId));
            const localSalesChannelID = channel ? toLocalID(channel.id) : null;
            if (!channel || localSalesChannelID === null || ['NONE', 'AMAZON'].includes(channel.ecommerceSettings?.ecommerceName ?? '')) return [];
            if (candidates.findIndex((item) => toLocalID(item.salesChannelId) === toLocalID(candidate.salesChannelId)) !== index) return [];
            return [
                {
                    key: JSON.stringify([state.product.id, channel.id, candidate.code]),
                    salesChannelID: channel.id,
                    localSalesChannelID,
                    sku: candidate.code,
                    label: channel.ecommerceSettings?.remoteUrl || channel.ecommerceSettings?.ecommerceName || channel.id,
                    isMain: candidate.isMain
                }
            ];
        });
    }, [salesChannels, state.salesChannelID, state.product.id, state.product.code, state.product.additionalCodes]);
    const [catalogs, setCatalogs] = useState<Record<string, VariantStoreOptions | undefined>>({});
    const onLoad = useCallback<OnLoad>((key, options) => {
        setCatalogs((current) => (current[key] === options ? current : { ...current, [key]: options }));
    }, []);
    const mainSource = sources.find((source) => source.isMain);
    const merchantProductOptions = (mainSource && catalogs[mainSource.key]) || emptyOptions;
    const merchantProductOptionsConnected = useMemo(
        () =>
            sources.flatMap<ConnectedVariantStoreOption>((source) =>
                source.isMain ? [] : (catalogs[source.key] ?? []).map((option) => ({ ...option, salesChannelID: source.localSalesChannelID, sku: source.sku }))
            ),
        [sources, catalogs]
    );
    return {
        merchantProductOptions,
        merchantProductOptionsConnected,
        mainOptionsReady: !mainSource || catalogs[mainSource.key] !== undefined,
        allOptionsReady: sources.every((source) => catalogs[source.key] !== undefined),
        optionChecks: (
            <Stack>
                {sources.map((source) => (
                    <ChannelOptions key={source.key} source={source} onLoad={onLoad} />
                ))}
            </Stack>
        )
    };
}
